import { ObservableBuilder, isAnyApiHandler, isPage } from "@madhive/mad-sdk";
// RSJ: feels like we shouldn't have the types in dependencies vs devDependencies so the eslint-disable-ing here unless there's a better solution
import { useState, useEffect, useMemo } from "react";
import { cleanupSubscribers, isObservable } from "../rxjs";
import { useDeepCompareEffect } from "../utils";
import { useListMadHooksUtils } from "./listUtils";
import { useMadHooksCacheSubscription } from "./useMadHooksCacheSubscription";
import { usePagination } from "./usePagination";
import { peerChangesHandler } from "../modules";
/**
 * Loads list data by collection and optional filter/sort
 * @param collection Collection the data belongs to
 * @param options Optional 'where'/'pagination' filters and sort to apply to madSDK
 * @returns ListData
 */
export const useLoadListData = (collection, options) => {
    const { filters, sort, pagination, setSort, setPageMetadata, onStalePage: setToPageZero } = usePagination(options);
    const { observer, handler } = useListMadHooksUtils(collection, { filters, sort });
    const [listData, setListData] = useState(observer.value);
    if (!isAnyApiHandler(handler)) {
        throw new Error("Handler is not an API handler");
    }
    const [count, setCount] = useState(0);
    const refresh = () => setCount((prevCount) => prevCount + 1);
    // ensures that pagination filters are updated when a new behavior subject is subscribed to.
    useEffect(() => {
        if (observer.value.pageMetadata) {
            setPageMetadata(observer.value.pageMetadata);
        }
    }, [observer]);
    useEffect(() => {
        if (!observer.value.isLoaded)
            return undefined;
        const idKey = handler.getIdentifierKey();
        const resourceId = observer.value.data?.length && observer.value.data[0]?.[idKey];
        if (!resourceId) {
            return undefined;
        }
        let subscription;
        peerChangesHandler
            .startListeningList(resourceId, handler.id, (saved) => {
            if (saved.heardAs === "new" ||
                observer.value.data?.some((item) => item[idKey] === saved.resourceId)) {
                observer.next({
                    ...observer.value,
                    isStale: true
                });
                return true;
            }
            return false;
        })
            .then((sub) => {
            subscription = sub;
        });
        return () => {
            subscription?.unsubscribe();
            peerChangesHandler.stopListening(resourceId);
        };
    }, [observer.value.isLoading, observer.value.isLoaded]);
    useMadHooksCacheSubscription(observer, (value) => {
        if (value.isStale && !value.isLoading) {
            refresh();
            if (options.paginate) {
                // any stale data means that current api pagination is also stale and must be reset to page 0
                setToPageZero();
            }
        }
        setListData({ ...value });
    }, [filters, sort]);
    // Dedicated cache (`behaviorSubjectMap`) cleanup.
    // This `useEffect` must happen *after* the `useMadHooksCacheSubscription` above that
    // handles the unsubscribing so that unobserved subjects can be deleted.
    useEffect(() => () => cleanupSubscribers(collection, { filters, sort }), []);
    /**
     * loads data from the api when not already loading
     */
    const load = () => {
        const current = observer.value;
        if (current.isInitializing && !current.isLoading && !options.notReady) {
            observer.next({
                ...current,
                isLoading: true,
                isInitializing: false,
                isLoaded: false,
                data: []
            });
            const result = handler.find(filters || { where: [] }, sort);
            if (isObservable(result) || result instanceof ObservableBuilder) {
                // handle Observable stuff
                result.subscribe({
                    next(value) {
                        if (isPage(value)) {
                            // ensures that pagination filters are updated when new page metadata is received from the API
                            setPageMetadata(value.page);
                            observer.next({
                                data: value.data,
                                pageMetadata: value.page,
                                isLoading: true,
                                isLoaded: false,
                                isDeleted: false,
                                isInitializing: false,
                                refresh
                            });
                        }
                        else {
                            observer.next({
                                data: Array.isArray(value) ? value : [value],
                                isLoading: true,
                                isLoaded: false,
                                isDeleted: false,
                                isInitializing: false,
                                refresh
                            });
                        }
                    },
                    complete() {
                        observer.next({
                            ...observer.value,
                            isLoading: false,
                            isLoaded: true,
                            isInitializing: false,
                            isStale: false
                        });
                    },
                    error(error) {
                        observer.next({
                            data: [],
                            isLoading: false,
                            isLoaded: false,
                            isDeleted: false,
                            isInitializing: false,
                            error,
                            refresh
                        });
                    }
                });
            }
            else {
                // handle Promise stuff
                result
                    .then((data) => {
                    observer.next({
                        data: Array.isArray(data) ? data : [data],
                        isLoading: false,
                        isLoaded: true,
                        isDeleted: false,
                        isStale: false,
                        isInitializing: false,
                        refresh
                    });
                })
                    .catch((error) => observer.next({
                    data: [],
                    isLoading: false,
                    isLoaded: false,
                    isDeleted: false,
                    isInitializing: false,
                    refresh,
                    error
                }));
            }
        }
    };
    // triggers data load when not already loading or loaded
    useDeepCompareEffect(() => {
        const current = observer.value;
        // calls madsdk if first observer
        if (current.isInitializing) {
            load();
        }
    }, [observer]);
    // used to trigger a reload of data
    useEffect(() => {
        if (count > 0) {
            observer.next({
                ...observer.value,
                isInitializing: true
            });
            load();
        }
    }, [count]);
    const byId = useMemo(() => observer.value.data?.reduce((acc, next) => {
        acc.set(handler.getIdentifier(next), next);
        return acc;
    }, new Map()), [observer.value.data, handler]);
    return {
        ...listData,
        byId,
        isLoading: listData.isInitializing || listData.isLoading,
        pagination,
        sort,
        setSort,
        refresh
    };
};
//# sourceMappingURL=useLoadListData.js.map